import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, filter, map, Observable, of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { CelumPropertiesProvider, DataUtil, isTruthy } from '@celum/core';

import { AccountAccess, AccountAccessStatus, License, LicenseType, UserDetails } from '../model/user.model';
import { LicenseService } from './license.service';
import { TenantService } from './tenant.service';

export interface UserServiceState {
  currentUser?: UserDetails;
}

@Injectable()
export class UserService extends ComponentStore<UserServiceState> {
  public currentUser$ = this.select(state => state.currentUser);
  public activeAccountAccesses$ = this.currentUser$.pipe(
    isTruthy(),
    map(user => user.accountAccesses.filter(accountAccess => accountAccess.status === AccountAccessStatus.ACTIVE))
  );

  public selectedAccount$: Observable<AccountAccess> = combineLatest([this.activeAccountAccesses$, this.tenantService.currentTenantId$]).pipe(
    map(([accountUsers, tenantId]) => accountUsers.find(accountUser => accountUser.accountId === tenantId))
  );

  /** Returns the current user and the current tenant. */
  public userInfo$ = combineLatest([this.currentUser$, this.selectedAccount$]).pipe(
    map(([user, tenant]) => ({
      user,
      tenant
    }))
  );

  constructor(
    private http: HttpClient,
    private tenantService: TenantService,
    private licenseService: LicenseService,
    private location: Location,
    private router: Router
  ) {
    super({});
  }

  public getCurrentUser(): UserDetails {
    return this.get().currentUser;
  }

  public loadCurrentUser(): Observable<void> {
    // Note: Do NOT catch an error here - this is called on several places, and depending on where it is called the error needs to be handled differently
    return this.http.get<UserDetails>(`${CelumPropertiesProvider.properties.authentication.saccUrl}/users/current`).pipe(
      tap(user => this.patchState({ currentUser: this.mapUser(user) })),
      switchMap(() => this.storeTenantIfUnset())
    );
  }

  public storeTenantIfUnset(): Observable<void> {
    const license = this.licenseService.applicationLicense;
    if (!license) {
      return of(void 0);
    }
    if (!this.tenantService.getCurrentTenantId() && !this.tenantService.getTenantIdFromUrl()) {
      return this.activeAccountAccesses$.pipe(
        map(accounts => accounts.filter(account => UserService.getValidLicenseByType(account, license))),
        map(accounts => accounts.sort((account1, account2) => account1.accountName.localeCompare(account2.accountName))),
        take(1),
        tap(accounts => {
          if (accounts.length > 0) {
            const tenantId = accounts[0].accountId;
            // Store the first item in the list from the BE
            this.tenantService.storeTenant(tenantId);
            // This is only needed right after login: Because the mechanism that uses APP_BASE_HREF to add the tenantId to the url has already run at application
            // startup, we have to update the url with the tenantId WITHOUT actually navigating somewhere. This will stay active until a refresh to apply this when
            // the user e.g. navigates to a subpage
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
              const finalDestination = (event as NavigationEnd).urlAfterRedirects;
              // Only add the tenantId if the app somehow is not able to add it by itself
              !window.location.href.includes('/tenant/') && this.location.replaceState(`/tenant/${tenantId}${finalDestination}`);
            });
          }
        }),
        map(() => void 0)
      );
    }

    return of(void 0);
  }

  public changeLocale(locale: string): Observable<UserDetails> {
    return this.http
      .put<UserDetails>(`${CelumPropertiesProvider.properties.authentication.saccUrl}/users/current`, {
        locale
      })
      .pipe(
        tap(user => {
          this.patchState({ currentUser: this.mapUser(user) });
        })
      );
  }

  private mapUser(response: UserDetails): UserDetails {
    return {
      admin: response.admin,
      invitations: response.invitations,
      trialPlan: response.trialPlan,
      userAccessToken: response.userAccessToken,
      oid: response.oid,
      status: response.status,
      firstName: response.firstName,
      lastName: response.lastName,
      email: response.email,
      locale: response.locale,
      profilePictureDownloadLink: response.profilePictureDownloadLink,
      accountAccesses: response?.accountAccesses.map((accountAccess: AccountAccess) => ({
        accountId: accountAccess.accountId,
        accountName: accountAccess.accountName,
        accountAccessToken: accountAccess.accountAccessToken,
        ownerOid: accountAccess.ownerOid,
        ownerName: accountAccess.ownerName,
        ownerEmail: accountAccess.ownerEmail,
        profilePictureDownloadLink: accountAccess.profilePictureDownloadLink,
        storageLimit: accountAccess.storageLimit,
        status: accountAccess.status,
        role: accountAccess.role,
        accountLogoDownloadLink: accountAccess.accountLogoDownloadLink,
        workroomPermissions: accountAccess.workroomPermissions,
        discriminator: accountAccess.discriminator,
        licenses: accountAccess.licenses.map((license: License) => ({
          type: license.type,
          active: license.active,
          experienceTenantAwareUrl: license.experienceTenantAwareUrl,
          experienceUrl: license.experienceUrl,
          librariesTenantAwareUrl: license.librariesTenantAwareUrl,
          librariesUrl: license.librariesUrl,
          wrTenantAwareUrl: license.wrTenantAwareUrl,
          wrUrl: license.wrUrl,
          chRepositories: license.chRepositories?.map(chRepository => ({
            repositoryId: chRepository.repositoryId,
            url: chRepository.url
          })),
          creationLimit: license.creationLimit,
          storageLimit: license.storageLimit
        })),
        experiencePermissions: accountAccess.experiencePermissions
      }))
    };
  }

  public static getValidLicenseByType(accountAccess: AccountAccess, licenseType: LicenseType): License {
    if (
      accountAccess.status !== AccountAccessStatus.ACTIVE ||
      licenseType === LicenseType.NO_LICENSE ||
      !UserService.hasValidAccountAccessPrivilege(accountAccess, licenseType)
    ) {
      return undefined;
    }

    return accountAccess.licenses.filter(license => license.active).find(license => license.type === licenseType);
  }

  public static hasValidAccountAccessPrivilege(accountAccess: AccountAccess, licenseType: LicenseType): boolean {
    /* 2 notes here:
     * Currently we check here whether the user has the privilege to use libraries/experience by checking the experiencePERMISSIONS field - this is a work in
     progress, and will be redone in the future
     * I went with a switch-case here, because with an if-statement IntelliJ was complaining about not being able to compare the licenseTypes because there was
     no overlap?
     */
    switch (licenseType) {
      case LicenseType.LIBRARIES:
      case LicenseType.EXPERIENCE:
        return !DataUtil.isEmpty(accountAccess.experiencePermissions);
      default:
        return true;
    }
  }
}
